/**
 * TypedFieldSelect — Shared dropdown for field mapping with type pills.
 *
 * Used by:
 * - InputMappingGrid (Eingabe-Tab sidebar)
 * - FieldMappingGrid (Ausgabe-Tab sidebar)
 * - InputMapper (Workflow Builder bottom dock)
 *
 * Shows a button trigger with selected value + TypeChip.
 * Dropdown options are grouped and each shows name + TypeChip + optional
 * compatibility badge.
 */

import {
  Menu,
  MenuItem,
  type MenuProps,
  MenuSeparator,
} from '@affine/component';
import { checkCompatibility } from '@streamlain/field-types';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { FC, KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CompatDot, TypeChip } from './type-chip';

// =============================================================================
// Types
// =============================================================================

export interface FieldOption {
  id: string;
  label: string;
  type: string;
}

export interface FieldOptionGroup {
  id: string;
  label: string;
  options: FieldOption[];
}

export interface TypedFieldSelectProps {
  /** Currently selected option ID (empty string = nothing selected). */
  value: string;
  /** Grouped options for the dropdown menu. */
  groups: FieldOptionGroup[];
  /** Called when the user picks an option. Empty string = cleared. */
  onChange: (optionId: string) => void;
  /** Placeholder when nothing is selected. */
  placeholder?: string;
  /** Target field type — used to compute compatibility badges. */
  targetType?: string;
  /** Optional domain-specific compatibility resolver. */
  getCompatibility?: (
    targetType: string,
    sourceType: string
  ) => 'compatible' | 'castable' | 'incompatible';
  /** Use compact sizing (for dock context). */
  compact?: boolean;
  /** Show search input at the top of the menu (for large option lists). */
  searchable?: boolean;
  /** Search placeholder text. */
  searchPlaceholder?: string;
  /** Show group label (node name) as prefix in each option row. */
  showGroupPrefix?: boolean;
}

// =============================================================================
// Menu positioning config (shared across all usages)
// =============================================================================

const menuContentOptions: MenuProps['contentOptions'] = {
  side: 'bottom' as const,
  align: 'start' as const,
  sideOffset: 4,
  style: {
    maxHeight: 'min(360px, calc(100vh - 170px))',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '2px',
    minWidth: '240px',
    maxWidth: 'min(400px, calc(100vw - 24px))',
  },
};

// =============================================================================
// Component
// =============================================================================

export const TypedFieldSelect: FC<TypedFieldSelectProps> = ({
  value,
  groups,
  onChange,
  placeholder = 'Select',
  targetType,
  getCompatibility = checkCompatibility,
  compact = false,
  searchable = false,
  searchPlaceholder = 'Felder suchen...',
  showGroupPrefix = false,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // sl-n5zg.2.1: React only to real open→close / close→open transitions.
  // The effect must not fire on initial mount when the menu has never been
  // opened, so we gate the cleanup-on-close branch with a ref that flips
  // the first time the menu is actually opened.
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (open) {
      hasOpenedRef.current = true;
      if (!searchable) return;
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    if (hasOpenedRef.current) {
      setSearch('');
    }
    return undefined;
  }, [open, searchable]);

  // sl-n5zg.2.1 (nit): memoize the selected-option lookup so we don't walk
  // the full `groups` array on every unrelated re-render.
  const selectedOption = useMemo<FieldOption | null>(() => {
    for (const group of groups) {
      const found = group.options.find(o => o.id === value);
      if (found) return found;
    }
    return null;
  }, [groups, value]);

  const selectedGroup = useMemo<FieldOptionGroup | null>(() => {
    for (const group of groups) {
      if (group.options.some(o => o.id === value)) return group;
    }
    return null;
  }, [groups, value]);

  // sl-n5zg.2.1: Extended filter — node name (group.label) also matches the
  // query, so typing a node label shows all of that node's options without
  // having to know any individual field name. Empty queries return all
  // groups unchanged.
  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups
      .map(g => {
        const groupLabelMatches = g.label.toLowerCase().includes(q);
        if (groupLabelMatches) return g;
        return {
          ...g,
          options: g.options.filter(
            o =>
              o.label.toLowerCase().includes(q) ||
              o.type.toLowerCase().includes(q)
          ),
        };
      })
      .filter(g => g.options.length > 0);
  }, [groups, search]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && search.length > 0) {
      e.stopPropagation();
      setSearch('');
    }
  };

  const fontSize = compact ? '12px' : '13px';
  const padding = compact ? '3px 6px' : '4px 8px';

  return (
    <Menu
      rootOptions={{ open, onOpenChange: setOpen }}
      items={
        <div>
          {/* Search input */}
          {searchable && (
            <div style={{ padding: '4px 8px' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={handleSearchKeyDown}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: '13px',
                  border: `1px solid ${cssVarV2('layer/insideBorder/border')}`,
                  borderRadius: '4px',
                  background: 'transparent',
                  color: 'inherit',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
          {/* None / clear option */}
          <MenuItem onClick={() => onChange('')}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: '6px',
              }}
            >
              <span
                style={{
                  width: '14px',
                  flexShrink: 0,
                  textAlign: 'center',
                  color: cssVarV2('button/primary'),
                  fontSize: '14px',
                }}
              >
                {!value ? '✓' : ''}
              </span>
              <span style={{ opacity: 0.6 }}>{placeholder}</span>
            </div>
          </MenuItem>
          {/* sl-n5zg.2.1: empty-state when the query filters everything out */}
          {searchable && search.length > 0 && filteredGroups.length === 0 && (
            <div
              style={{
                padding: '12px 16px',
                fontSize: '12px',
                opacity: 0.6,
                textAlign: 'center',
              }}
            >
              Keine Felder gefunden
            </div>
          )}
          {/* Grouped options */}
          {filteredGroups.map(group => (
            <div key={group.id}>
              <MenuSeparator />
              {filteredGroups.length > 1 && (
                <MenuItem disabled>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      opacity: 0.5,
                    }}
                  >
                    {group.label}
                  </span>
                </MenuItem>
              )}
              {group.options.map(opt => {
                const compat = targetType
                  ? getCompatibility(targetType, opt.type)
                  : 'compatible';
                const isSelected = opt.id === value;
                return (
                  <MenuItem key={opt.id} onClick={() => onChange(opt.id)}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minWidth: 0,
                        gap: '6px',
                      }}
                    >
                      {/* Selection indicator (left column) */}
                      <span
                        style={{
                          width: '14px',
                          flexShrink: 0,
                          textAlign: 'center',
                          color: cssVarV2('button/primary'),
                          fontSize: '14px',
                        }}
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {showGroupPrefix && (
                          <span style={{ opacity: 0.5, fontSize: '11px' }}>
                            {group.label}
                            {' › '}
                          </span>
                        )}
                        {opt.label}
                      </span>
                      <TypeChip type={opt.type} small={compact} />
                      {/* Compat indicator — always shown */}
                      <CompatDot
                        compatibility={compat}
                        style={{ marginLeft: '2px' }}
                      />
                    </div>
                  </MenuItem>
                );
              })}
            </div>
          ))}
        </div>
      }
      contentOptions={menuContentOptions}
    >
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding,
          fontSize,
          borderRadius: '4px',
          border: 'none',
          backgroundColor: cssVarV2('layer/background/hoverOverlay'),
          color: cssVarV2('text/primary'),
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
          overflow: 'hidden',
        }}
      >
        {selectedOption ? (
          <>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {showGroupPrefix && selectedGroup && (
                <span style={{ opacity: 0.5 }}>
                  {selectedGroup.label}
                  {' › '}
                </span>
              )}
              {selectedOption.label}
            </span>
            <TypeChip
              type={selectedOption.type}
              small={compact}
              style={{ marginLeft: 0 }}
            />
          </>
        ) : (
          <span style={{ flex: 1, opacity: 0.5 }}>{placeholder}</span>
        )}
        <span
          style={{
            color: cssVarV2('text/tertiary'),
            fontSize: '10px',
            flexShrink: 0,
          }}
        >
          ▼
        </span>
      </button>
    </Menu>
  );
};
